(function () {
  'use strict';

  const YOUTUBE_SVG_PLAY =
    '<svg viewBox="0 0 68 48"><path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="#e00"/><path d="M45 24 27 14v20" fill="#fff"/></svg>';

  const YOUTUBE_SVG_ICON =
    '<svg viewBox="0 0 28 20"><path d="M27.37 3.1A3.52 3.52 0 0 0 24.9.6C22.7 0 14 0 14 0S5.3 0 3.1.6A3.52 3.52 0 0 0 .63 3.1C0 5.3 0 10 0 10s0 4.7.63 6.9a3.52 3.52 0 0 0 2.47 2.5C5.3 20 14 20 14 20s8.7 0 10.9-.6a3.52 3.52 0 0 0 2.47-2.5C28 14.7 28 10 28 10s0-4.7-.63-6.9z" fill="currentColor"/><path d="M11.2 14.2 18.5 10l-7.3-4.2v8.4z" fill="#fff"/></svg>';

  function formatCount(n) {
    if (n == null) return '0';
    n = parseInt(n, 10);
    if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
    return n.toLocaleString();
  }

  function formatDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  }

  function parseDuration(iso) {
    const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!m) return '0:00';
    const h = parseInt(m[1] || 0, 10);
    const min = parseInt(m[2] || 0, 10);
    const sec = parseInt(m[3] || 0, 10);
    const parts = [];
    if (h) {
      parts.push(h);
      parts.push(String(min).padStart(2, '0'));
    } else {
      parts.push(min);
    }
    parts.push(String(sec).padStart(2, '0'));
    return parts.join(':');
  }

  async function apiFetch(path, apiKey) {
    const url = `https://www.googleapis.com/youtube/v3/${path}&key=${apiKey}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`YouTube API error ${res.status}`);
    return res.json();
  }

  async function loadPlaylist(container, apiKey, playlistId, perPage) {
    try {
      const playlistData = await apiFetch(
        `playlists?part=snippet&id=${playlistId}`,
        apiKey
      );
      const channelId = playlistData.items?.[0]?.snippet?.channelId;

      const [channelData, firstPage] = await Promise.all([
        channelId
          ? apiFetch(`channels?part=snippet,statistics&id=${channelId}`, apiKey)
          : null,
        apiFetch(
          `playlistItems?part=snippet&playlistId=${playlistId}&maxResults=50`,
          apiKey
        ),
      ]);

      let allItems = firstPage.items || [];
      let nextToken = firstPage.nextPageToken;
      while (nextToken) {
        const page = await apiFetch(
          `playlistItems?part=snippet&playlistId=${playlistId}&maxResults=50&pageToken=${nextToken}`,
          apiKey
        );
        allItems = allItems.concat(page.items || []);
        nextToken = page.nextPageToken;
      }

      allItems = allItems.filter(
        (i) => i.snippet?.resourceId?.videoId && i.snippet.title !== 'Private video'
      );

      const videoIds = allItems.map((i) => i.snippet.resourceId.videoId);
      const videoMap = {};
      for (let i = 0; i < videoIds.length; i += 50) {
        const batch = videoIds.slice(i, i + 50).join(',');
        const vData = await apiFetch(
          `videos?part=snippet,contentDetails,statistics&id=${batch}`,
          apiKey
        );
        (vData.items || []).forEach((v) => (videoMap[v.id] = v));
      }

      const videos = allItems
        .map((item) => {
          const vid = item.snippet.resourceId.videoId;
          const detail = videoMap[vid];
          if (!detail) return null;
          return {
            id: vid,
            title: detail.snippet.title,
            description: detail.snippet.description,
            thumbnail:
              detail.snippet.thumbnails?.maxres?.url ||
              detail.snippet.thumbnails?.high?.url ||
              detail.snippet.thumbnails?.medium?.url,
            publishedAt: detail.snippet.publishedAt,
            duration: parseDuration(detail.contentDetails?.duration || ''),
            views: detail.statistics?.viewCount,
            likes: detail.statistics?.likeCount,
            comments: detail.statistics?.commentCount,
          };
        })
        .filter(Boolean);

      render(container, channelData?.items?.[0] || null, videos, perPage);
    } catch (err) {
      container.innerHTML = `<div class="ptz-yt-error">Unable to load playlist. ${err.message}</div>`;
      console.error('[ptz-youtube-playlist]', err);
    }
  }

  function renderHeader(channel) {
    if (!channel) return '';
    const s = channel.snippet;
    const st = channel.statistics;
    const avatar =
      s.thumbnails?.medium?.url || s.thumbnails?.default?.url || '';
    return `
      <div class="ptz-yt-header">
        ${avatar ? `<img class="ptz-yt-header__avatar" src="${avatar}" alt="" loading="lazy">` : ''}
        <div class="ptz-yt-header__info">
          <h2 class="ptz-yt-header__name">${s.title}</h2>
          <div class="ptz-yt-header__stats">
            <span>${formatCount(st.subscriberCount)} Subscribers</span>
            <span>${formatCount(st.videoCount)} Videos</span>
            <span>${formatCount(st.viewCount)} Views</span>
          </div>
        </div>
        <a
          class="ptz-yt-header__subscribe"
          href="https://www.youtube.com/channel/${channel.id}?sub_confirmation=1"
          target="_blank"
          rel="noopener"
        >
          ${YOUTUBE_SVG_ICON} ${formatCount(st.subscriberCount)}
        </a>
      </div>`;
  }

  function renderCard(video) {
    return `
      <a
        class="ptz-yt-card"
        href="https://www.youtube.com/watch?v=${video.id}"
        target="_blank"
        rel="noopener"
      >
        <div class="ptz-yt-card__thumb">
          <img src="${video.thumbnail}" alt="" loading="lazy">
          <div class="ptz-yt-card__play">${YOUTUBE_SVG_PLAY}</div>
          ${video.duration ? `<span class="ptz-yt-card__duration">${video.duration}</span>` : ''}
        </div>
        <div class="ptz-yt-card__body">
          <h3 class="ptz-yt-card__title">${video.title}</h3>
          ${video.description ? `<p class="ptz-yt-card__desc">${video.description}</p>` : ''}
          <div class="ptz-yt-card__date">${formatDate(video.publishedAt)}</div>
          <div class="ptz-yt-card__meta">
            <span>${formatCount(video.views)} Views</span>
            <span>${formatCount(video.likes)} Likes</span>
            <span>${formatCount(video.comments)} Comments</span>
          </div>
        </div>
      </a>`;
  }

  function renderPagination(currentPage, totalPages, onPage) {
    if (totalPages <= 1) return '';
    const wrapper = document.createElement('div');
    wrapper.className = 'ptz-yt-pagination';

    const prev = document.createElement('button');
    prev.innerHTML = '&#8249;';
    prev.disabled = currentPage === 1;
    prev.addEventListener('click', () => onPage(currentPage - 1));
    wrapper.appendChild(prev);

    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);

    for (let i = start; i <= end; i++) {
      const btn = document.createElement('button');
      btn.textContent = i;
      if (i === currentPage) btn.setAttribute('aria-current', 'page');
      btn.addEventListener('click', () => onPage(i));
      wrapper.appendChild(btn);
    }

    const next = document.createElement('button');
    next.innerHTML = '&#8250;';
    next.disabled = currentPage === totalPages;
    next.addEventListener('click', () => onPage(currentPage + 1));
    wrapper.appendChild(next);

    return wrapper;
  }

  function render(container, channel, videos, perPage) {
    const totalPages = Math.ceil(videos.length / perPage);
    let currentPage = 1;

    function draw() {
      const start = (currentPage - 1) * perPage;
      const pageVideos = videos.slice(start, start + perPage);

      container.innerHTML = '';

      const headerHtml = renderHeader(channel);
      if (headerHtml) container.insertAdjacentHTML('beforeend', headerHtml);

      const grid = document.createElement('div');
      grid.className = 'ptz-yt-grid';
      grid.innerHTML = pageVideos.map(renderCard).join('');
      container.appendChild(grid);

      const pag = renderPagination(currentPage, totalPages, (page) => {
        currentPage = page;
        draw();
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      if (pag) container.appendChild(pag);
    }

    draw();
  }

  function showSkeleton(container, count) {
    let cards = '';
    for (let i = 0; i < count; i++) {
      cards += `
        <div class="ptz-yt-skeleton__card">
          <div class="ptz-yt-skeleton__thumb"></div>
          <div class="ptz-yt-skeleton__line"></div>
          <div class="ptz-yt-skeleton__line ptz-yt-skeleton__line--short"></div>
        </div>`;
    }
    container.innerHTML = `<div class="ptz-yt-skeleton">${cards}</div>`;
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-ptz-yt-playlist]').forEach(function (el) {
      const apiKey = el.dataset.ptzYtApiKey;
      const playlistId = el.dataset.ptzYtPlaylist;
      const perPage = parseInt(el.dataset.ptzYtPerPage, 10) || 6;

      if (!apiKey || !playlistId) {
        el.innerHTML =
          '<div class="ptz-yt-error">YouTube API Key and Playlist ID are required. Configure them in the theme editor.</div>';
        return;
      }

      showSkeleton(el, perPage);
      loadPlaylist(el, apiKey, playlistId, perPage);
    });
  });
})();
